import { useState, useEffect } from 'react';
import {
  BellIcon,
  ShieldExclamationIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  UserGroupIcon,
  ShareIcon,
  TrashIcon,
  CheckIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useNotifications } from '../../context/NotificationContext';
import { notificationsAPI } from '../../services/api';

const typeIcons = {
  security_alert: ShieldExclamationIcon,
  task_assigned: ClipboardDocumentCheckIcon,
  task_due_soon: ClockIcon,
  mfa_compliance_drop: UserGroupIcon,
  public_file_detected: ShareIcon,
  weekly_summary: BellIcon,
};

const typeColors = {
  security_alert: 'text-red-500 bg-red-50 dark:bg-red-900/20',
  task_assigned: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
  task_due_soon: 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
  mfa_compliance_drop: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20',
  public_file_detected: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20',
  weekly_summary: 'text-green-500 bg-green-50 dark:bg-green-900/20',
};

const preferenceLabels = {
  securityAlert: { label: 'Security Alerts', description: 'High/critical security alerts detected' },
  taskAssigned: { label: 'Task Assignments', description: 'When a task is assigned to you' },
  taskDueSoon: { label: 'Task Due Soon', description: 'Reminders for tasks due within 7 days' },
  mfaComplianceDrop: { label: 'MFA Compliance Drop', description: 'When MFA compliance falls below 100%' },
  publicFileDetected: { label: 'Public File Sharing', description: 'External or anonymous file sharing detected' },
  weeklySummary: { label: 'Weekly Summary', description: 'Weekly email digest of security metrics' },
};

function formatDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export default function NotificationsPage() {
  const [activeTab, setActiveTab] = useState('notifications');
  const {
    notifications,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    unreadCount,
  } = useNotifications();

  const [preferences, setPreferences] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sendingMFA, setSendingMFA] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [triggerMessage, setTriggerMessage] = useState('');

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    if (activeTab === 'preferences' && !preferences) {
      loadPreferences();
    }
  }, [activeTab]);

  const loadNotifications = async () => {
    const result = await fetchNotifications(1);
    if (result) {
      setHasMore(result.pagination?.page < result.pagination?.pages);
    }
  };

  const loadMore = async () => {
    const nextPage = page + 1;
    const result = await fetchNotifications(nextPage);
    if (result) {
      setPage(nextPage);
      setHasMore(result.pagination?.page < result.pagination?.pages);
    }
  };

  const loadPreferences = async () => {
    try {
      setPrefsLoading(true);
      const response = await notificationsAPI.getPreferences();
      setPreferences(response.data.data);
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setPrefsLoading(false);
    }
  };

  const updatePreference = async (key, value) => {
    if (!preferences) return;

    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);

    try {
      setPrefsSaving(true);
      await notificationsAPI.updatePreferences({ [key]: value });
    } catch (error) {
      console.error('Failed to update preference:', error);
      // Revert on error
      setPreferences(preferences);
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleTriggerMFAAlert = async () => {
    setSendingMFA(true);
    setTriggerMessage('');
    try {
      const response = await notificationsAPI.triggerMFAAlert();
      setTriggerMessage(response.data.message);
    } catch (error) {
      setTriggerMessage('Failed to send MFA alerts.');
    } finally {
      setSendingMFA(false);
    }
  };

  const handleTriggerWeeklySummary = async () => {
    setSendingSummary(true);
    setTriggerMessage('');
    try {
      const response = await notificationsAPI.triggerWeeklySummary();
      setTriggerMessage(response.data.message);
    } catch (error) {
      setTriggerMessage('Failed to send weekly summary.');
    } finally {
      setSendingSummary(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Notifications</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Stay updated on security alerts and task assignments
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setActiveTab('notifications')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'notifications'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <BellIcon className="h-4 w-4 inline mr-2" />
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
              {unreadCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('preferences')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'preferences'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <Cog6ToothIcon className="h-4 w-4 inline mr-2" />
          Preferences
        </button>
      </div>

      {activeTab === 'notifications' && (
        <Card padding={false}>
          {/* Actions bar */}
          {notifications.length > 0 && unreadCount > 0 && (
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <button
                onClick={markAllAsRead}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
              >
                <CheckIcon className="h-4 w-4" />
                Mark all as read
              </button>
            </div>
          )}

          {loading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" text="Loading notifications..." />
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-12 text-center">
              <BellIcon className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white">No notifications</h3>
              <p className="mt-1 text-slate-600 dark:text-slate-400">
                You're all caught up! Check back later for updates.
              </p>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                {notifications.map((notification) => {
                  const Icon = typeIcons[notification.type] || BellIcon;
                  const colorClass = typeColors[notification.type] || 'text-slate-500 bg-slate-50 dark:bg-slate-700';

                  return (
                    <li
                      key={notification.id}
                      className={`p-4 ${!notification.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 p-2 rounded-lg ${colorClass}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className={`font-medium ${
                                !notification.read
                                  ? 'text-slate-900 dark:text-white'
                                  : 'text-slate-700 dark:text-slate-300'
                              }`}>
                                {notification.title}
                              </p>
                              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                                {notification.message}
                              </p>
                              <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                                {formatDate(notification.createdAt)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {!notification.read && (
                                <button
                                  onClick={() => markAsRead(notification.id)}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                  title="Mark as read"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => deleteNotification(notification.id)}
                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {hasMore && (
                <div className="p-4 text-center border-t border-slate-200 dark:border-slate-700">
                  <Button variant="outline" onClick={loadMore} loading={loading}>
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {activeTab === 'preferences' && (
        <Card>
          {prefsLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" text="Loading preferences..." />
            </div>
          ) : !preferences ? (
            <div className="py-12 text-center">
              <p className="text-slate-600 dark:text-slate-400">Failed to load preferences</p>
              <Button onClick={loadPreferences} className="mt-4">
                Retry
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Choose how you want to receive notifications. Changes are saved automatically.
                {prefsSaving && <span className="ml-2 text-blue-600">Saving...</span>}
              </p>

              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {Object.entries(preferenceLabels).map(([key, { label, description }]) => {
                  const appKey = `${key}App`;
                  const emailKey = `${key}Email`;
                  const hasAppPref = appKey in preferences;
                  const hasEmailPref = emailKey in preferences || key === 'weeklySummary';

                  return (
                    <div key={key} className="py-4 first:pt-0 last:pb-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-slate-900 dark:text-white">{label}</h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{description}</p>
                        </div>
                        <div className="flex items-center gap-6">
                          {hasAppPref && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={preferences[appKey]}
                                onChange={(e) => updatePreference(appKey, e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-600 dark:text-slate-400">In-app</span>
                            </label>
                          )}
                          {hasEmailPref && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={preferences[key === 'weeklySummary' ? 'weeklySummaryEmail' : emailKey]}
                                onChange={(e) => updatePreference(
                                  key === 'weeklySummary' ? 'weeklySummaryEmail' : emailKey,
                                  e.target.checked
                                )}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-600 dark:text-slate-400">Email</span>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Email Actions */}
      <Card>
        <Card.Header>
          <Card.Title>Send Email Reports</Card.Title>
          <Card.Description>Manually trigger email alerts and summaries to your inbox.</Card.Description>
        </Card.Header>

        {triggerMessage && (
          <div className="mb-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3">
            <p className="text-sm text-blue-700 dark:text-blue-400">{triggerMessage}</p>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
            <div className="flex items-center gap-3 mb-3">
              <ShieldExclamationIcon className="h-6 w-6 text-red-500" />
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white">MFA Alert</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Send alert emails for users without MFA
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleTriggerMFAAlert}
              loading={sendingMFA}
            >
              Send MFA Alerts
            </Button>
          </div>

          <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
            <div className="flex items-center gap-3 mb-3">
              <BellIcon className="h-6 w-6 text-green-500" />
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white">Weekly Summary</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Send security posture summary to your email
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleTriggerWeeklySummary}
              loading={sendingSummary}
            >
              Send Weekly Summary
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
